import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { 
  Mail, 
  Phone, 
  Clock, 
  MapPin,
  ArrowRight
} from 'lucide-react';
import { contactInfo } from '../data/mock';

const Contact = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section 
        className="py-20"
        style={{ background: 'var(--gradient-hero-warm)' }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <motion.h1
              className="heading-hero mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Contact Us
            </motion.h1>
            <motion.p
              className="body-large"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Have questions or need assistance? We're here to help. 
              Reach out to us through any of the channels below.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Options */}
      <section className="py-20 bg-[#FFF9F2]">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Phone */}
            <motion.a
              href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-full bg-[#FCC9C7] flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <Phone className="w-7 h-7 text-[#232323]" />
              </div>
              <h3 className="heading-2 mb-2">Call Us</h3>
              <p className="text-xl font-medium text-[#232323] mb-2">
                {contactInfo.phone}
              </p>
              <p className="text-[#666]">
                Speak directly with our team
              </p>
            </motion.a>

            {/* WhatsApp */}
            <motion.a
              href={`https://web.whatsapp.com/send?phone=919619998944&text=${encodeURIComponent('Hello, I would like to request concierge support for a senior.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 rounded-full bg-[#b8d1ba] flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <svg className="w-7 h-7 text-[#232323]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <h3 className="heading-2 mb-2">WhatsApp</h3>
              <p className="text-xl font-medium text-[#232323] mb-2">
                Chat with us
              </p>
              <p className="text-[#666]">
                Quick responses, anytime
              </p>
            </motion.a>

            {/* Email */}
            <motion.a
              href={`mailto:${contactInfo.email}`}
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow group"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-14 h-14 rounded-full bg-[#E4EDF8] flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <Mail className="w-7 h-7 text-[#232323]" />
              </div>
              <h3 className="heading-2 mb-2">Email</h3>
              <p className="text-xl font-medium text-[#232323] mb-2">
                {contactInfo.email}
              </p>
              <p className="text-[#666]">
                We reply within 24 hours
              </p>
            </motion.a>

            {/* Hours */}
            <motion.div
              className="bg-white rounded-2xl p-8 shadow-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="w-14 h-14 rounded-full bg-[#FEEFDC] flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-[#232323]" />
              </div>
              <h3 className="heading-2 mb-2">Working Hours</h3>
              <p className="text-xl font-medium text-[#232323] mb-2">
                {contactInfo.hours}
              </p>
              <p className="text-[#666]">
                Available every day of the week
              </p>
            </motion.div>
          </div>

          {/* CTA Card */}
          <motion.div
            className="bg-[#232323] rounded-2xl p-10 text-center text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-semibold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Fill out our help request form and we'll contact you to discuss 
              your needs and provide a personalized quote.
            </p>
            <Link 
              to="/request-help" 
              className="inline-flex items-center gap-2 bg-white text-[#232323] rounded-full px-8 py-4 font-mono text-sm uppercase tracking-wide hover:bg-gray-100 transition-colors"
            >
              Request Help Now
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;